import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Phone, CheckCircle, ChevronDown, Check,
  Monitor, Server, ShieldCheck, MailCheck, PenTool, ArrowLeftRight,
  Palette, Layout, Zap, Globe, FileText, MapPin,
  MessageSquare, Receipt, Headphones, BarChart3, Bot, Lightbulb,
  ExternalLink, Clock, Users, ChevronRight, Wrench,
} from "lucide-react";
import SEO from "../components/SEO";
import Footer from "../components/Footer";
import HorizontalTimeline from "../components/HorizontalTimeline";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API = API_BASE + "/api";

/* ════════════════════════════════════════
   DATA
   ════════════════════════════════════════ */

const serviceCategories = [
  { icon: Monitor, label: "Website Design", href: "/website-design", color: "from-blue-600 to-blue-700" },
  { icon: PenTool, label: "Website Redesign", href: "/website-redesign", color: "from-indigo-600 to-indigo-700" },
  { icon: Layout, label: "Landing Pages", href: "/landing-pages", color: "from-violet-600 to-violet-700" },
  { icon: Server, label: "Managed Hosting", href: "/managed-hosting", color: "from-emerald-600 to-emerald-700" },
  { icon: ShieldCheck, label: "Website Care Plans", href: "/website-care-plans", color: "from-teal-600 to-teal-700" },
  { icon: Zap, label: "Website Optimisation", href: "/website-optimisation", color: "from-amber-600 to-amber-700" },
  { icon: ArrowLeftRight, label: "Website Transfers", href: "/website-transfers", color: "from-cyan-600 to-cyan-700" },
  { icon: ShieldCheck, label: "Security Audits", href: "/security-audits", color: "from-red-600 to-red-700" },
  { icon: Headphones, label: "One-Off Support", href: "/one-off-website-support", color: "from-orange-600 to-orange-700" },
  { icon: MailCheck, label: "Business Email", href: "/business-email-setup", color: "from-sky-600 to-sky-700" },
  { icon: Globe, label: "Google Business Profile", href: "/google-business-profile", color: "from-green-600 to-green-700" },
  { icon: BarChart3, label: "Website Reporting", href: "/website-reporting", color: "from-purple-600 to-purple-700" },
  { icon: FileText, label: "Content Writing", href: "/content-writing", color: "from-pink-600 to-pink-700" },
  { icon: Bot, label: "Website Assistants", href: "/website-assistants", color: "from-slate-600 to-slate-700" },
];

const servicesOverview = [
  {
    title: "Design & Build",
    desc: "Custom websites, redesigns and focused landing pages built around your business.",
    items: [
      { name: "Website Design", href: "/website-design", price: "From £650" },
      { name: "Website Redesign", href: "/website-redesign", price: "Quoted" },
      { name: "Landing Pages", href: "/landing-pages", price: "From £199" },
    ],
  },
  {
    title: "Hosting & Management",
    desc: "Secure, managed hosting with proactive maintenance and hassle-free migrations.",
    items: [
      { name: "Managed Hosting", href: "/managed-hosting", price: "From £15/mo" },
      { name: "Website Care Plans", href: "/website-care-plans", price: "From £99/mo" },
      { name: "Website Transfers", href: "/website-transfers", price: "From £200" },
    ],
  },
  {
    title: "Support & Performance",
    desc: "Security reviews, speed optimisation and flexible one-off help when you need it.",
    items: [
      { name: "Security Audits", href: "/security-audits", price: "From £149" },
      { name: "Website Optimisation", href: "/website-optimisation", price: "£149 one-off" },
      { name: "One-Off Support", href: "/one-off-website-support", price: "From £50" },
    ],
  },
  {
    title: "Business Services",
    desc: "Email setup, Google visibility, automated reporting and professional content.",
    items: [
      { name: "Business Email Setup", href: "/business-email-setup", price: "£99 setup" },
      { name: "Google Business Profile", href: "/google-business-profile", price: "£129 setup" },
      { name: "Website Reporting", href: "/website-reporting", price: "£149 setup" },
      { name: "Content Writing", href: "/content-writing", price: "From £59" },
      { name: "Website Assistants", href: "/website-assistants", price: "From £129" },
    ],
  },
];

const whyReasons = [
  { icon: MessageSquare, title: "Direct Communication", desc: "Speak directly with the person working on your website. No account managers or unnecessary layers." },
  { icon: Receipt, title: "Transparent Pricing", desc: "Clear pricing agreed before work begins. No hidden costs or changing estimates." },
  { icon: Palette, title: "Built Around Your Business", desc: "Every website is designed around your services, customers and goals — not forced into a template." },
  { icon: Headphones, title: "Support Beyond Launch", desc: "Hosting, maintenance, care plans and ongoing support are all available when you need them." },
  { icon: Users, title: "Small Business Focus", desc: "We work with startups and small to medium-sized businesses. We understand the priorities and budgets involved." },
  { icon: Lightbulb, title: "Practical, Honest Advice", desc: "Straightforward recommendations based on what your business actually needs, not what we want to sell." },
];

const projects = [
  {
    name: "SendSam",
    url: "https://sendsam.co.uk",
    description: "A clear, approachable website designed to make complex SEND support easier to understand for families and schools, with dedicated service pathways and strong enquiry-focused calls to action.",
    image: "https://customer-assets.emergentagent.com/job_fast-wordpress-build/artifacts/xbjdf2rh_sendsam.png",
    label: "Website Design",
  },
  {
    name: "JP Engineering",
    url: "https://jp-engineering.co.uk",
    description: "A professional recruitment website built to clearly present specialist engineering sectors, strengthen credibility and help candidates and employers quickly find the information they need.",
    image: "https://customer-assets.emergentagent.com/job_fast-wordpress-build/artifacts/1t9888yn_Screenshot%202026-04-01%20220844.png",
    label: "Website Design",
  },
  {
    name: "Custora",
    url: "https://custora.uk",
    description: "A modern business website built to communicate payroll, HR and compliance services clearly, helping businesses understand their options and take the next step.",
    image: `${API}/static/custora.webp`,
    label: "Website Design",
  },
];

const popularPricing = [
  {
    name: "Website Design",
    subtitle: "Starter",
    price: "£650",
    features: ["Tailored Website Design", "Fully Responsive", "Contact Forms", "On-Page SEO Setup", "14 Days Post-Launch Support"],
    href: "/website-design",
    popular: false,
  },
  {
    name: "Website Design",
    subtitle: "Professional",
    price: "£850",
    features: ["Everything in Starter", "12 Months Hosting", "Domain Registration", "SSL Certificate", "30 Days Post-Launch Support"],
    href: "/website-design",
    popular: true,
  },
  {
    name: "Landing Pages",
    subtitle: "",
    price: "£199",
    features: ["One-Page Design", "Responsive Layout", "Lead Capture Form", "Clear Call to Action", "Hosting Guidance"],
    href: "/landing-pages",
    popular: false,
  },
  {
    name: "Managed Hosting",
    subtitle: "",
    price: "£15/mo",
    features: ["UK WordPress Hosting", "SSL Certificate", "Daily Backups", "Uptime Monitoring", "Technical Support"],
    href: "/managed-hosting",
    popular: false,
  },
];

const oneOffItems = [
  { label: "Website Design", price: "From £650", href: "/website-design" },
  { label: "Website Redesign", price: "Quoted", href: "/website-redesign" },
  { label: "Landing Pages", price: "From £199", href: "/landing-pages" },
  { label: "Security Audits", price: "From £149", href: "/security-audits" },
  { label: "Website Optimisation", price: "£149", href: "/website-optimisation" },
  { label: "One-Off Support", price: "From £50", href: "/one-off-website-support" },
  { label: "Business Email Setup", price: "£99", href: "/business-email-setup" },
  { label: "Google Business Profile", price: "From £129", href: "/google-business-profile" },
  { label: "Website Transfers", price: "From £200", href: "/website-transfers" },
];

const ongoingItems = [
  { label: "Managed Hosting", price: "From £15/mo", href: "/managed-hosting" },
  { label: "Website Care Plans", price: "From £99/mo", href: "/website-care-plans" },
  { label: "Website Reporting", price: "From £19.99/mo", href: "/website-reporting" },
  { label: "Content Writing", price: "From £49/mo", href: "/content-writing" },
  { label: "Website Assistants", price: "From £12.99/mo", href: "/website-assistants" },
  { label: "Google Profile Management", price: "From £12.99/mo", href: "/google-business-profile" },
  { label: "Business Email Support", price: "From £6.99/mo", href: "/business-email-setup" },
];

/* Banked — hidden for now */
// eslint-disable-next-line no-unused-vars
const testimonials = [
  { name: "Local Business Owner", text: "CtrlAltComplete took the time to understand our business and built a website that genuinely represents what we do. The process was straightforward and communication was excellent throughout." },
  { name: "Startup Founder", text: "We needed a professional website quickly without the usual agency runaround. The pricing was clear from the start and the end result exceeded our expectations." },
  { name: "SME Director", text: "Having hosting, maintenance and support all in one place makes everything so much easier. We don't have to worry about the technical side — it's all taken care of." },
];

const faqs = [
  { q: "What services does CtrlAltComplete offer?", a: "We offer a comprehensive range of web design and digital services including website design, redesign, landing pages, managed hosting, website care plans, security audits, website optimisation, one-off support, business email setup, Google Business Profile management, website reporting, content writing and website assistants." },
  { q: "How much does a website cost?", a: "Website pricing depends on the size and requirements of the project. Our standard website packages start from £650, with anything outside those packages quoted before work begins. All pricing is transparent and agreed upfront." },
  { q: "Do you only build websites?", a: "No. While website design is a core service, we also offer ongoing hosting and maintenance, security audits, performance optimisation, business email setup, content writing, website reporting and more. We're here to help with your wider digital presence." },
  { q: "Can you help with an existing website?", a: "Yes. Whether you need a redesign, performance improvements, security review, content updates or ongoing support, we can help with existing websites as well as building new ones." },
  { q: "Do you offer ongoing support?", a: "Yes. Website Care Plans, managed hosting, content writing plans and website reporting are all available on an ongoing basis. One-off support is also available for individual tasks." },
  { q: "Where are you based?", a: "We're based in the West Midlands, with a registered office in Birmingham. We work with businesses locally and across the UK." },
];

const orbitServices = [
  { icon: Layout, label: "Web Design", color: "#3b82f6" },
  { icon: Server, label: "Managed Hosting", color: "#10b981" },
  { icon: Wrench, label: "Website Support", color: "#f59e0b" },
  { icon: ShieldCheck, label: "Security", color: "#ef4444" },
  { icon: MailCheck, label: "Microsoft 365", color: "#8b5cf6" },
  { icon: MapPin, label: "Google Business", color: "#22c55e" },
];


/* ════════════════════════════════════════
   ORBIT HERO
   ════════════════════════════════════════ */

const OrbitHero = ({ onEnquiry }) => {
  const [revealed, setRevealed] = useState(false);
  useEffect(() => { const t = setTimeout(() => setRevealed(true), 100); return () => clearTimeout(t); }, []);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-24 pb-16 px-6 md:px-12 xl:px-24 overflow-hidden" style={{ background: "linear-gradient(135deg, #f8f9ff 0%, #eef1fb 40%, #f4f4f5 100%)" }}>
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle, #002FA7 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      <div className="max-w-7xl mx-auto w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-20 items-center">
          {/* Left content */}
          <div className={`transition-all duration-700 ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-primary mb-6">Web Design & Digital Services</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-extrabold leading-[1.08] text-foreground mb-6">
              Everything Your<br />Business Needs<br /><span className="text-primary">Online</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">Website design, hosting, security, optimisation and ongoing support — all from one team that understands small business.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={onEnquiry} data-testid="hero-enquiry-btn" className="btn-primary px-8 py-4 text-base font-bold inline-flex items-center justify-center gap-2">Start Your Project <ArrowRight className="w-5 h-5" /></button>
              <a href="#Our-Services" data-testid="hero-services-btn" className="btn-secondary px-8 py-4 text-base font-bold inline-flex items-center justify-center gap-2">Explore Services</a>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-7 gap-y-3" data-testid="hero-trust-signals">
              {["UK-Based Support", "Transparent Pricing", "No Long-Term Contracts"].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <CheckCircle className="w-[18px] h-[18px] text-primary flex-shrink-0" strokeWidth={2} />
                  <span className="text-[15px] font-medium text-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Orbit */}
          <div className={`relative flex items-center justify-center transition-all duration-1000 delay-300 ${revealed ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
            <div className="orbit-container" data-testid="orbit-animation">
              {/* Single orbit path */}
              <div className="orbit-path" />

              {/* Realistic Laptop */}
              <div className="laptop-wrap">
                <div className="laptop-lid">
                  <div className="laptop-cam" />
                  <div className="laptop-screen">
                    <div className="lp-site">
                      <div className="lp-nav">
                        <div className="lp-logo" /><div className="lp-links"><i /><i /><i /></div><div className="lp-cta" />
                      </div>
                      <div className="lp-hero">
                        <div className="lp-tag" />
                        <div className="lp-h1" /><div className="lp-h1b" />
                        <div className="lp-sub" />
                        <div className="lp-btns"><div className="lp-btn-p" /><div className="lp-btn-s" /></div>
                      </div>
                      <div className="lp-cards"><div className="lp-card lpc-b"><div className="lpc-ico" /><div className="lpc-t" /></div><div className="lp-card lpc-g"><div className="lpc-ico" /><div className="lpc-t" /></div><div className="lp-card lpc-v"><div className="lpc-ico" /><div className="lpc-t" /></div></div>
                      <div className="lp-stats"><div className="lp-stat" /><div className="lp-stat" /><div className="lp-stat" /></div>
                      <div className="lp-foot" />
                    </div>
                  </div>
                </div>
                <div className="laptop-base"><div className="laptop-notch" /></div>
              </div>

              {/* Orbiting tiles — single path, 3D depth */}
              {orbitServices.map((svc, i) => (
                <div
                  key={svc.label}
                  className="orbit-item"
                  style={{ animationDelay: `${-i * 10}s` }}
                  data-testid={`orbit-tile-${svc.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="orbit-tile" style={{ "--accent": svc.color }}>
                    <div className="tile-ico" style={{ background: `${svc.color}18`, color: svc.color }}>
                      <svc.icon className="w-[18px] h-[18px]" strokeWidth={2} />
                    </div>
                    <span className="tile-label">{svc.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


/* ════════════════════════════════════════
   MAIN HOMEPAGE
   ════════════════════════════════════════ */

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState(null);

  const openEnquiry = () => window.dispatchEvent(new CustomEvent('openEnquiry'));

  // Scroll reveal
  const revealRefs = useRef([]);
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) { revealRefs.current.forEach((el) => { if (el) el.classList.add("hp-revealed"); }); return; }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("hp-revealed"); obs.unobserve(e.target); } });
    }, { threshold: 0.1 });
    revealRefs.current.forEach((el) => { if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);
  const addRevealRef = (el) => { if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el); };

  return (
    <>
      <SEO title="Web Design & Digital Services West Midlands | CtrlAltComplete" description="West Midlands-based web design and digital services for businesses across the UK, including websites, hosting, support, security, Microsoft 365 and more." path="/" noAppendBrand />

      {/* 1. HERO */}
      <OrbitHero onEnquiry={openEnquiry} />


      {/* 2. SERVICE CATEGORIES — double size */}
      <section ref={addRevealRef} data-testid="service-categories" className="hp-section py-20 md:py-28 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-primary mb-4">What We Do</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-extrabold leading-tight text-foreground mb-4">Web Design & Digital Services</h2>
            <p className="text-base md:text-lg text-muted-foreground">Everything your business needs to build, manage and grow its online presence — all in one place.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {serviceCategories.map((svc) => (
              <Link key={svc.label} to={svc.href} data-testid={`cat-${svc.label.toLowerCase().replace(/\s+/g, "-")}`} className="group relative bg-white border border-border rounded-2xl p-6 flex flex-col items-center text-center gap-3 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:border-primary/20">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${svc.color} flex items-center justify-center transition-transform duration-200 group-hover:scale-110`}>
                  <svc.icon className="w-6 h-6 text-white" strokeWidth={1.75} />
                </div>
                <span className="text-sm font-bold text-foreground leading-tight group-hover:text-primary transition-colors">{svc.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* 3. SERVICES OVERVIEW */}
      <section ref={addRevealRef} id="Our-Services" data-testid="services-overview" className="hp-section py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-secondary">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-primary mb-4">Our Services</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-extrabold leading-tight text-foreground mb-4">Everything You Need to Grow Online</h2>
            <p className="text-base md:text-lg text-muted-foreground">From website design and hosting to performance, security and business tools.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {servicesOverview.map((group) => (
              <div key={group.title} data-testid={`svc-group-${group.title.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white border border-border rounded-2xl p-7 transition-all duration-200 hover:shadow-lg hover:border-primary/15">
                <h3 className="text-xl font-extrabold text-foreground mb-2">{group.title}</h3>
                <p className="text-sm text-muted-foreground mb-5">{group.desc}</p>
                <div className="space-y-2.5">
                  {group.items.map((item) => (
                    <Link key={item.name} to={item.href} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/80 transition-colors group/item">
                      <span className="text-sm font-semibold text-foreground group-hover/item:text-primary transition-colors">{item.name}</span>
                      <div className="flex items-center gap-2">
                        {item.price && <span className="text-xs font-bold text-primary bg-primary/5 px-2.5 py-1 rounded-full">{item.price}</span>}
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover/item:text-primary transition-colors" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* 4. WHY CTRLALTCOMPLETE — centred text, 3-col grid */}
      <section ref={addRevealRef} id="Why-Choose-Us" data-testid="why-choose-us-section" className="hp-section py-20 md:py-28 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-primary mb-4">Why CtrlAltComplete</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-extrabold leading-tight text-foreground mb-4">Straightforward Web Services Without the Complication</h2>
            <p className="text-base md:text-lg text-muted-foreground mb-6">Direct communication, transparent pricing and services built around what your business actually needs.</p>
            <button onClick={openEnquiry} data-testid="why-cta-btn" className="btn-primary px-6 py-3 text-sm font-bold inline-flex items-center gap-2">Get in Touch <ArrowRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyReasons.map((reason, i) => (
              <div key={reason.title} data-testid={`why-card-${i}`} className="group bg-white border border-border rounded-2xl p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:border-primary/20">
                <div className="w-11 h-11 bg-primary/10 text-primary flex items-center justify-center rounded-xl mb-4 transition-transform duration-200 group-hover:scale-105">
                  <reason.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="text-base font-bold text-foreground mb-1.5 group-hover:text-primary transition-colors">{reason.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{reason.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* 5. FEATURED PROJECTS — card grid like WebsiteDesignPage */}
      <section ref={addRevealRef} data-testid="featured-projects" className="hp-section py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-secondary">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-primary mb-4">Featured Work</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-extrabold leading-tight text-foreground mb-4">Websites Built for Real Businesses</h2>
            <p className="text-base md:text-lg text-muted-foreground">Real projects designed and built to help businesses strengthen their online presence.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {projects.map((project, i) => (
              <div key={project.name} data-testid={`featured-project-${i}`} className="group bg-white border border-border rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <a href={project.url} target="_blank" rel="noopener noreferrer" className="block overflow-hidden">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={project.image} alt={`${project.name} website`} width={800} height={450} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[450ms] group-hover:scale-[1.035]" />
                  </div>
                </a>
                <div className="p-6">
                  <span className="inline-block text-[11px] tracking-[0.15em] uppercase font-bold text-primary mb-2">{project.label}</span>
                  <h3 className="text-lg font-bold text-foreground mb-2">{project.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{project.description}</p>
                  <a href={project.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-bold text-primary transition-all group-hover:gap-2.5">
                    View Project <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/portfolio" className="inline-flex items-center gap-2 text-base font-bold text-primary hover:gap-3 transition-all" data-testid="view-portfolio-btn">View Our Work <ArrowRight className="w-5 h-5" /></Link>
          </div>
        </div>
      </section>


      {/* 6. POPULAR SERVICES PRICING */}
      <section ref={addRevealRef} id="pricing" data-testid="pricing-section" className="hp-section py-20 md:py-28 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-primary mb-4">Popular Services</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-extrabold leading-tight text-foreground mb-4">Clear, Transparent Pricing</h2>
            <p className="text-base md:text-lg text-muted-foreground">Straightforward pricing with no hidden costs. Every project is agreed before work begins.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {popularPricing.map((plan, i) => (
              <div key={`${plan.name}-${plan.subtitle}`} data-testid={`pricing-card-${i}`} className={`pricing-card bg-white p-7 flex flex-col rounded-[10px] ${plan.popular ? "price-card-popular" : "border border-border"}`} data-label={plan.popular ? "Recommended" : ""}>
                <p className="text-xs font-bold text-primary uppercase tracking-wider mb-1">{plan.name}</p>
                {plan.subtitle && <h3 className="text-lg font-extrabold text-foreground mb-1">{plan.subtitle}</h3>}
                <div className="flex items-baseline gap-1 mb-5"><span className="text-3xl font-extrabold text-foreground">{plan.price}</span></div>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {plan.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2.5 text-sm">
                      <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5 pricing-check" />
                      <span className="text-foreground">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to={plan.href} data-testid={`pricing-cta-${i}`} className={`pricing-cta block w-full text-center py-2.5 text-sm font-bold mt-auto rounded-[10px] transition-colors ${plan.popular ? "btn-primary" : "bg-foreground text-white hover:bg-zinc-800"}`}>Explore {plan.subtitle || plan.name}</Link>
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground text-center mt-8">All prices apply to standard brochure-style websites. Bespoke projects are quoted separately.</p>
        </div>
      </section>


      {/* 7. HOW IT WORKS — horizontal timeline */}
      <HorizontalTimeline id="How-It-Works" extraClass="bg-secondary" />


      {/* 8. ONE-OFF vs ONGOING */}
      <section ref={addRevealRef} data-testid="oneoff-vs-ongoing" className="hp-section py-20 md:py-28 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-primary mb-4">Flexible Options</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-extrabold leading-tight text-foreground mb-4">One-Off or Ongoing — Your Choice</h2>
            <p className="text-base md:text-lg text-muted-foreground">Whether you need a single project or continuous support, we have services to suit.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div data-testid="oneoff-panel" className="bg-white border border-border rounded-2xl p-7">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-foreground text-white flex items-center justify-center rounded-xl"><Zap className="w-5 h-5" /></div>
                <h3 className="text-xl font-extrabold text-foreground">One-Off Services</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-5">Fixed-price projects with clear scope. Pay once and the work is done.</p>
              <div className="space-y-2">
                {oneOffItems.map((item) => (
                  <Link key={item.label} to={item.href} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/80 transition-colors group/oo">
                    <span className="text-sm font-medium text-foreground group-hover/oo:text-primary transition-colors">{item.label}</span>
                    <span className="text-xs font-bold text-primary">{item.price}</span>
                  </Link>
                ))}
              </div>
            </div>
            <div data-testid="ongoing-panel" className="bg-white border-2 border-primary rounded-2xl p-7 relative">
              <div className="absolute -top-3 left-6 bg-primary text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1">Build & Grow</div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-primary text-white flex items-center justify-center rounded-xl"><Clock className="w-5 h-5" /></div>
                <h3 className="text-xl font-extrabold text-foreground">Ongoing Services</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-5">Monthly or annual plans for continuous hosting, maintenance, reporting and content.</p>
              <div className="space-y-2">
                {ongoingItems.map((item) => (
                  <Link key={item.label} to={item.href} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-primary/[0.03] transition-colors group/og">
                    <span className="text-sm font-medium text-foreground group-hover/og:text-primary transition-colors">{item.label}</span>
                    <span className="text-xs font-bold text-primary">{item.price}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* 9. TESTIMONIALS — banked / hidden for now */}


      {/* 10. FAQ */}
      <section ref={addRevealRef} data-testid="homepage-faq" className="hp-section py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-secondary">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-primary mb-4">Common Questions</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-extrabold leading-tight text-foreground mb-4">Frequently Asked Questions</h2>
            <p className="text-base md:text-lg text-muted-foreground">Quick answers to the things we're most commonly asked.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={faq.q} className="border border-border rounded-xl overflow-hidden" data-testid={`homepage-faq-${i}`}>
                  <button onClick={() => setOpenFaq(isOpen ? null : i)} className={`w-full px-6 py-4 flex items-center justify-between text-left transition-colors cursor-pointer ${isOpen ? "bg-primary/[0.04]" : "bg-white hover:bg-secondary/50"}`}>
                    <span className={`font-semibold pr-4 transition-colors ${isOpen ? "text-primary" : "text-foreground"}`}>{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 flex-shrink-0 text-primary transition-transform duration-250 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  <div className="grid transition-all duration-250" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
                    <div className="overflow-hidden">
                      <div className="px-6 py-4 bg-white border-t border-border"><p className="text-base text-muted-foreground leading-relaxed">{faq.a}</p></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link to="/faq" className="inline-flex items-center gap-2 text-base font-bold text-primary hover:gap-3 transition-all">View All FAQs <ArrowRight className="w-5 h-5" /></Link>
          </div>
        </div>
      </section>


      {/* 11. FINAL CTA */}
      <section ref={addRevealRef} data-testid="final-cta" className="hp-section py-20 md:py-24 px-6 md:px-12 lg:px-24 bg-primary text-white text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4">Ready to Get Started?</h2>
          <p className="text-lg text-white/80 mb-8">Tell us what your business needs and we'll help you find the right service.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={openEnquiry} data-testid="cta-enquiry-btn" className="bg-white text-primary hover:bg-white/90 px-8 py-4 font-bold inline-flex items-center justify-center gap-2 rounded-[10px] transition-colors">Send an Enquiry <ArrowRight className="w-5 h-5" /></button>
            <a href="tel:01217943680" className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 font-bold inline-flex items-center justify-center gap-2 rounded-[10px] transition-colors"><Phone className="w-5 h-5" /> Call Us Now</a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
